import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Sparkles, Coins, ArrowLeft, Send, Loader2, Calendar, Trash2, Plus, Minus, Gem, Zap, LayoutGrid, Pickaxe, Info, ChevronUp, X, CheckCircle2 } from "lucide-react";
import OreStoreAvailabilityWidget from "../components/OreStoreAvailabilityWidget";
import MeetingTimePickerInline from "../components/MeetingTimePickerInline";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Assets
const ORESTORE_LOGO = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/3azwctos_Ore-Store-LOGO2.png";
const STORE_SPRITE = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/dv6lmfw5_sprite-max-px-36.gif";
const WOODEN_BOARD = "https://customer-assets.emergentagent.com/job_refinery-hub-4/artifacts/coeq1hmg_woodenboard.gif";

const getGuestId = () => {
  let guestId = localStorage.getItem("guest_id");
  if (!guestId) {
    guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("guest_id", guestId);
  }
  return guestId;
};

const formatMesos = (n) => {
  const v = Math.floor(Number(n) || 0);
  return v.toLocaleString("en-US").replace(/,/g, ".");
};

const CATEGORY_ICONS = {
  All: LayoutGrid,
  Ores: Pickaxe,
  Plates: Gem,
  Crystals: Zap,
};

// =========================== ITEM CARD ===========================
const ItemCard = ({ item, amount, onChange, onAddFly, expert }) => {
  const cardRef = useRef(null);

  // Spawn N fly animations (capped at 25). Used when adding 1 (+) or many (typed).
  const spawnFly = (count) => {
    if (!cardRef.current || !onAddFly) return;
    const n = Math.min(Math.max(0, Math.floor(count)), 25);
    if (n <= 0) return;
    const rect = cardRef.current.getBoundingClientRect();
    for (let i = 0; i < n; i++) {
      // small stagger + random offset so ores fan out instead of stacking on a single point
      const jitterX = (Math.random() - 0.5) * Math.min(rect.width * 0.6, 60);
      const jitterY = (Math.random() - 0.5) * Math.min(rect.height * 0.4, 30);
      setTimeout(() => {
        onAddFly({
          image_url: item.image_url,
          mesos: item.buy_price,
          startX: rect.left + rect.width / 2 + jitterX,
          startY: rect.top + rect.height / 2 + jitterY,
        });
      }, i * 40);
    }
  };

  const setAmount = (v) => {
    let n = Math.max(0, parseInt(v) || 0);
    if (n > 9999) n = 9999;
    const prev = amount || 0;
    onChange(item.name, n);
    const delta = n - prev;
    if (delta > 0) spawnFly(delta);
  };
  const dec = () => {
    const prev = amount || 0;
    onChange(item.name, Math.max(0, prev - 1));
  };
  const inc = () => {
    const prev = amount || 0;
    if (prev >= 9999) return;
    onChange(item.name, Math.min(9999, prev + 1));
    spawnFly(1);
  };

  const active = amount > 0;

  return (
    <div
      ref={cardRef}
      className={`relative rounded-md border-2 transition overflow-hidden select-none ${
        active
          ? "border-orange-400 bg-[#fff3d4] shadow-[0_0_0_2px_#5a3a14_inset]"
          : "border-[#5a3a14] bg-[#f4e1bb] hover:border-orange-500 hover:bg-[#fff3d4]"
      }`}
      style={{ boxShadow: active ? "0 4px 0 #3b2509" : "0 3px 0 #3b2509" }}
    >
      <div className="p-2 flex flex-col items-center gap-1">
        {/* Item icon with maple-style sunken frame */}
        <div className="relative w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded bg-[#1a1108] border-2 border-[#5a3a14] shadow-inner">
          <img
            src={item.image_url}
            alt={item.name}
            className="max-w-[40px] max-h-[40px] sm:max-w-[44px] sm:max-h-[44px] object-contain"
            style={{ imageRendering: "pixelated" }}
            onError={(e) => { e.target.style.opacity = 0.3; }}
          />
          {item.bonus_per_unit > 0 && (
            <div className="absolute -top-1.5 -right-1.5 bg-fuchsia-600 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow-[0_2px_0_#3b2509] border border-fuchsia-200 flex items-center gap-0.5">
              <Sparkles className="w-2.5 h-2.5" />+{item.bonus_per_unit}%
            </div>
          )}
        </div>

        {/* Name */}
        <div className="text-center w-full">
          <div className="text-[#3b2509] font-extrabold text-[11px] sm:text-xs leading-tight truncate" title={item.name} style={{ fontFamily: "'Comic Sans MS', 'Segoe UI', sans-serif" }}>
            {item.name}
          </div>
          <div className="text-[#7a4f1d] text-[10px] sm:text-[11px] font-bold mt-0.5 flex items-center justify-center gap-0.5">
            <Coins className="w-3 h-3" />
            {formatMesos(item.buy_price)}
          </div>
        </div>

        {/* Amount controls */}
        <div className="flex items-center gap-1 w-full">
          <button
            type="button"
            onClick={dec}
            className="shrink-0 w-6 h-7 flex items-center justify-center rounded bg-[#a05a1f] hover:bg-[#c47326] active:translate-y-[1px] border-2 border-[#5a3a14] text-white disabled:opacity-30 shadow-[0_2px_0_#3b2509]"
            disabled={!amount}
            aria-label="Decrease"
          >
            <Minus className="w-3 h-3" />
          </button>
          <input
            type="number"
            min="0"
            max="9999"
            value={amount || ""}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className="min-w-0 flex-1 w-full h-7 text-center text-sm font-extrabold rounded bg-white border-2 border-[#5a3a14] text-[#3b2509] focus:outline-none focus:border-orange-500 px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            type="button"
            onClick={inc}
            className="shrink-0 w-6 h-7 flex items-center justify-center rounded bg-[#3a8b3a] hover:bg-[#4caf50] active:translate-y-[1px] border-2 border-[#1d4a1d] text-white shadow-[0_2px_0_#1d4a1d]"
            aria-label="Increase"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>

        {amount > 0 && (
          <div className="text-[10px] text-[#3b2509] font-bold font-mono truncate w-full text-center">
            = {formatMesos(item.buy_price * amount)}
          </div>
        )}

        {/* Expert overlay (admins only): sell price + stock */}
        {expert && (
          <div className="w-full mt-0.5 px-1 py-0.5 rounded bg-[#1a0e05] border border-amber-500/60 text-[9px] leading-tight font-mono text-amber-200 grid grid-cols-2 gap-x-1" data-testid={`expert-info-${item.name}`}>
            <span className="text-amber-300/80">SELL</span>
            <span className="text-right text-yellow-300 font-bold">{formatMesos(item.sell_price || 0)}</span>
            <span className="text-amber-300/80">STOCK</span>
            <span className={`text-right font-bold ${(item.stock || 0) <= 0 ? "text-red-400" : (item.stock || 0) <= 50 ? "text-orange-300" : "text-emerald-300"}`}>
              {item.stock != null ? formatMesos(item.stock) : "—"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// =========================== FLY ANIMATION OVERLAY ===========================
// Tracks ore-spawn animations: ore icon flies from item card to store sprite,
// while a +mesos label drifts upward and fades.
const FlyOverlay = ({ animations, onAnimationEnd, storePoint }) => {
  return (
    <div className="fixed inset-0 pointer-events-none z-[60]">
      {animations.map((anim) => {
        const dx = storePoint ? storePoint.x - anim.startX : 0;
        const dy = storePoint ? storePoint.y - anim.startY : 0;
        return (
          <div key={anim.id}>
            {/* Flying ore icon */}
            <img
              src={anim.image_url}
              alt=""
              onAnimationEnd={() => onAnimationEnd(anim.id)}
              className="absolute w-7 h-7 object-contain os-fly-ore"
              style={{
                left: `${anim.startX}px`,
                top: `${anim.startY}px`,
                imageRendering: "pixelated",
                "--dx": `${dx}px`,
                "--dy": `${dy}px`,
                filter: "drop-shadow(0 0 6px rgba(255,200,80,0.9))",
              }}
            />
            {/* Floating mesos label */}
            <div
              className="absolute font-extrabold text-yellow-300 os-fly-mesos pointer-events-none"
              style={{
                left: `${anim.startX}px`,
                top: `${anim.startY}px`,
                fontFamily: "'Comic Sans MS', sans-serif",
                textShadow: "2px 2px 0 #3b2509, 0 0 8px rgba(255,200,80,0.7)",
                fontSize: "14px",
                whiteSpace: "nowrap",
              }}
            >
              +{formatMesos(anim.mesos)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =========================== HEADER BANNER ===========================
const HeaderBanner = ({ data }) => (
  <div
    className="mb-4 sm:mb-6 relative overflow-hidden rounded-lg border-4 border-[#5a3a14] bg-[#1f1208]"
    style={{ boxShadow: "0 6px 0 #3b2509, 0 0 30px rgba(255,140,40,0.25)" }}
  >
    {/* Wood plank top edge */}
    <div className="h-2 w-full" style={{ background: "repeating-linear-gradient(90deg, #8b5a2b 0 30px, #6b3f12 30px 60px)" }} />

    <div className="flex items-center justify-center gap-4 px-3 py-3 sm:px-6 sm:py-4 bg-[#2a1a0e]">
      <img
        src={ORESTORE_LOGO}
        alt="OreStore"
        className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto object-contain"
        style={{ imageRendering: "auto", filter: "drop-shadow(0 0 12px rgba(255,160,40,0.5))" }}
      />
    </div>

    {/* Mission / info text */}
    <div className="px-4 py-3 bg-[#2a1a0e] border-t-2 border-[#5a3a14] text-orange-100/95 text-[12px] sm:text-[13px] leading-relaxed space-y-2">
      <p>
        <strong className="text-orange-200">Ore Store's main mission</strong> is to make all minerals, raw and refined available in one place, everyday. To offer a constant stable service for the community &amp; help you save owls.
      </p>
      <p>
        We are always at the same place at <strong className="text-yellow-300">Free Market 15 on Channel 15</strong>. Our stock is replenished everyday, and sometimes few times a day if there is need for that.
      </p>
      <p>
        Stock is replenished mainly at <strong className="text-yellow-300">22:00 – 01:00 server time</strong>. We also buy all kind of ores, raw and refined, jewels and crystals, rocks. You can find more information about this down below.
      </p>
      <p className="italic text-orange-200/90">
        Prices are reflective of demand and the hard work it is to have store the up 24/7 fully stocked.
      </p>

      {/* Major outlined disclaimer */}
      <div
        className="mt-3 px-4 py-3 rounded-md border-4 border-yellow-400 bg-gradient-to-r from-[#3b2509] via-[#5a3a14] to-[#3b2509] text-center"
        style={{ boxShadow: "0 0 0 2px #3b2509 inset, 0 4px 0 #1a0e05" }}
        data-testid="orestore-buy-disclaimer"
      >
        <p
          className="text-yellow-200 font-extrabold tracking-wide text-[13px] sm:text-[14px] uppercase"
          style={{
            fontFamily: "'Comic Sans MS', 'Segoe UI', sans-serif",
            textShadow: "2px 2px 0 #3b2509, -1px -1px 0 #3b2509, 1px -1px 0 #3b2509, -1px 1px 0 #3b2509",
            letterSpacing: "0.5px",
          }}
        >
          We will be happy to buy all your ores!
        </p>
        <p className="text-orange-100/95 text-[11px] sm:text-[12px] mt-1 font-bold">
          Submit your offer and we will contact you shortly to arrange the meeting.
        </p>
      </div>
    </div>

    <div className="px-3 py-2 bg-[#3a2814] border-t-2 border-[#5a3a14]">
      <div className="flex flex-wrap items-center justify-center gap-2 text-[11px] sm:text-xs">
        <span className="px-2 py-1 rounded bg-[#a44dba] text-white font-bold border-2 border-[#5a235e] shadow-[0_2px_0_#3b1042]">
          <Sparkles className="inline w-3 h-3 mr-1" />
          Max bonus {data.max_bonus_capacity}%
        </span>
        <span className="px-2 py-1 rounded bg-[#d68a26] text-white font-bold border-2 border-[#5a3a14] shadow-[0_2px_0_#3b2509]">
          <Pickaxe className="inline w-3 h-3 mr-1" />
          {(data.categories.Ores?.length || 0) + (data.categories.Plates?.length || 0) + (data.categories.Crystals?.length || 0)} items
        </span>
        {!data.store_active && (
          <span className="px-2 py-1 rounded bg-red-600 text-white font-bold border-2 border-red-900">⚠ CLOSED</span>
        )}
      </div>
    </div>

    {/* Wood plank bottom edge */}
    <div className="h-2 w-full" style={{ background: "repeating-linear-gradient(90deg, #8b5a2b 0 30px, #6b3f12 30px 60px)" }} />
  </div>
);

// =========================== STORE SIDE SPRITE (right) ===========================
const StoreSidePanel = ({ storeRef }) => (
  <div className="hidden lg:flex flex-col items-center gap-3 sticky top-4">
    {/* Wooden board sign — FM 15 / CH 15 */}
    <img
      src={WOODEN_BOARD}
      alt="Free Market 15 - Channel 15"
      className="w-44 h-auto object-contain os-board-sway"
      style={{ imageRendering: "auto" }}
    />
    {/* Store sprite (animated) */}
    <div className="relative" ref={storeRef}>
      <img
        src={STORE_SPRITE}
        alt="OreStore"
        className="w-56 h-auto object-contain"
        style={{ imageRendering: "auto", filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.5))" }}
      />
    </div>
  </div>
);

// =========================== OFFER PANEL (sidebar/drawer body) ===========================
const OfferPanel = ({
  calc, amounts, setAmount, contactIgn, setContactIgn, contactDiscord, setContactDiscord,
  notes, setNotes, user, navigate, submitOffer, submitting, data,
  bonusGuilds, selectedGuildId, setSelectedGuildId, expertMode,
}) => {
  return (
    <div className="space-y-3">
      {/* Selected items list */}
      <div className="max-h-56 overflow-y-auto pr-1 space-y-1.5">
        {calc.breakdown.length === 0 ? (
          <div className="text-center text-[#a98856] text-sm py-6 italic">
            Pick items from the catalog →
          </div>
        ) : (
          calc.breakdown.map((b) => (
            <div key={b.name} className="flex items-center gap-2 bg-[#3a2814] rounded p-1.5 border border-[#5a3a14] text-xs">
              <img src={b.image_url} alt={b.name} className="w-7 h-7 object-contain" style={{ imageRendering: "pixelated" }} />
              <div className="flex-1 min-w-0">
                <div className="text-[#fff3d4] truncate font-bold">{b.name}</div>
                <div className="text-[#e1b066] font-mono">×{b.amount}</div>
              </div>
              <div className="text-yellow-200 font-mono text-right font-bold">
                {formatMesos(b.subtotal)}
              </div>
              <button
                onClick={() => setAmount(b.name, 0)}
                className="text-red-400 hover:text-red-300"
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Totals */}
      <div className="space-y-1.5 border-t-2 border-[#5a3a14] pt-3 text-sm">
        <div className="flex justify-between">
          <span className="text-[#e1b066]">Base total</span>
          <span className="font-mono text-[#fff3d4] font-bold">{formatMesos(calc.totalBase)}</span>
        </div>
        {calc.guildBonusPct > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-sky-300 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Guild bonus ({calc.guildName})
            </span>
            <span className="font-mono text-sky-200">+{calc.guildBonusPct}%</span>
          </div>
        )}
        {calc.itemBonusPct > 0 && calc.guildBonusPct > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-fuchsia-300 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Item bonus
            </span>
            <span className="font-mono text-fuchsia-200">+{calc.itemBonusPct}%</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-fuchsia-300 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />
            Total bonus {calc.bonusPctRaw > calc.maxBonus ? (
              <span className="text-fuchsia-200 text-xs">({calc.bonusPctRaw}% → cap {calc.maxBonus}%)</span>
            ) : (
              <span className="text-xs">({calc.bonusPct}%)</span>
            )}
          </span>
          <span className="font-mono text-fuchsia-200 font-bold">+{formatMesos(calc.bonusAmt)}</span>
        </div>
        <div className="flex justify-between text-base font-extrabold pt-2 border-t border-[#5a3a14]/50">
          <span className="text-[#fff3d4]">Total payout</span>
          <span className="font-mono text-yellow-300" style={{ textShadow: "2px 2px 0 #3b2509" }}>
            {formatMesos(calc.totalPayout)}
          </span>
        </div>
      </div>

      {/* EXPERT MODE — admin-only profitability summary */}
      {expertMode && calc.breakdown.length > 0 && (
        <div
          className="rounded-md border-2 border-amber-400/70 bg-gradient-to-br from-[#1f1208] to-[#2a1a0e] p-3 space-y-1 shadow-[0_3px_0_#3b2509]"
          data-testid="orestore-expert-summary"
        >
          <div className="text-[10px] uppercase tracking-wider font-extrabold text-amber-300 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Expert — Profitability (Admin only)
          </div>
          <div className="text-sm space-y-1 font-mono">
            <div className="flex justify-between">
              <span className="text-amber-200/80">Sell gross (no tax)</span>
              <span className="text-yellow-300 font-bold">{formatMesos(calc.totalSellGross)}</span>
            </div>
            <div className="flex justify-between text-xs text-rose-300">
              <span>− Tax ({calc.taxPct}%)</span>
              <span>−{formatMesos(calc.taxAmt)}</span>
            </div>
            <div className="flex justify-between border-t border-amber-700/40 pt-1">
              <span className="text-amber-200/80">Sell after tax</span>
              <span className="text-yellow-200 font-bold">{formatMesos(calc.sellAfterTax)}</span>
            </div>
            <div className="flex justify-between text-xs text-orange-200/80">
              <span>− Buy payout (already shown above)</span>
              <span>−{formatMesos(calc.totalPayout)}</span>
            </div>
            <div className="flex justify-between border-t-2 border-amber-500/60 pt-1.5">
              <span className="font-extrabold text-amber-300">Profit</span>
              <span
                className={`font-extrabold ${calc.profit >= 0 ? "text-emerald-300" : "text-red-400"}`}
                style={{ textShadow: "1px 1px 0 #3b2509" }}
              >
                {calc.profit >= 0 ? "+" : ""}{formatMesos(calc.profit)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-amber-200/80">Margin (vs. buy)</span>
              <span className={`font-bold ${calc.margin >= 0 ? "text-emerald-300" : "text-red-400"}`}>
                {calc.margin >= 0 ? "+" : ""}{calc.margin}%
              </span>
            </div>
          </div>
          <div className="text-[10px] text-amber-200/60 italic mt-1">
            Sell uses each item's effective price (active special_price honored). Tax applied to gross sell. Profit = sell after tax − final buy payout (incl. bonus).
          </div>
        </div>
      )}

      {/* Bonus Guild selector */}
      {bonusGuilds && bonusGuilds.length > 0 && (
        <div className="pt-3 border-t-2 border-[#5a3a14] space-y-2" data-testid="orestore-guild-selector">
          <div className="text-xs text-[#e1b066] font-extrabold uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />
            Bonus Guild Membership
          </div>
          <select
            value={selectedGuildId || ""}
            onChange={(e) => setSelectedGuildId(e.target.value)}
            className="w-full h-9 px-2 rounded bg-white border-2 border-[#5a3a14] text-[#3b2509] font-bold text-sm focus:outline-none focus:border-orange-500"
            data-testid="orestore-guild-select"
          >
            <option value="">— No guild (no extra bonus) —</option>
            {bonusGuilds.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} (+{g.bonus_pct}%)
              </option>
            ))}
          </select>
          <div className="text-[10px] sm:text-[11px] leading-snug text-orange-200/90 bg-[#3a2814] border border-[#a05a1f] rounded p-2 italic">
            <strong className="text-yellow-300 not-italic">Honor-based:</strong> select your guild only if you are an active in-game member. Your membership will be verified at the meeting; the bonus may be denied if your character is not part of the declared guild. Combined bonus (items + guild) is capped at <strong className="text-yellow-300 not-italic">{calc.maxBonus}%</strong>.
          </div>
        </div>
      )}

      {/* Contact */}
      <div className="pt-3 border-t-2 border-[#5a3a14] space-y-2">
        <div className="text-xs text-[#e1b066] font-extrabold uppercase tracking-wider">Contact info</div>
        <div>
          <Label className="text-[#fff3d4] text-xs font-bold">In-Game Name (IGN)</Label>
          <Input
            value={contactIgn}
            onChange={(e) => setContactIgn(e.target.value.slice(0, 30))}
            placeholder="e.g. SilentBard"
            className="bg-white border-2 border-[#5a3a14] text-[#3b2509] mt-1 h-9 font-bold"
          />
        </div>
        <div>
          <Label className="text-[#fff3d4] text-xs font-bold">Discord (optional)</Label>
          <Input
            value={contactDiscord}
            onChange={(e) => setContactDiscord(e.target.value.slice(0, 64))}
            placeholder={user?.auth_provider === "discord" ? user.username : "e.g. you#0001"}
            className="bg-white border-2 border-[#5a3a14] text-[#3b2509] mt-1 h-9 font-bold"
          />
        </div>
        <div>
          <Label className="text-[#fff3d4] text-xs font-bold">Notes (optional)</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="Anything the buyer should know"
            className="w-full mt-1 px-2 py-1.5 text-sm rounded bg-white border-2 border-[#5a3a14] text-[#3b2509] focus:outline-none focus:border-orange-500 resize-none font-medium"
          />
        </div>
      </div>

      <Button
        onClick={submitOffer}
        disabled={submitting || calc.breakdown.length === 0 || (!contactIgn && !contactDiscord) || !data.store_active}
        className="w-full mt-3 h-11 font-extrabold bg-[#ff8a1a] hover:bg-[#ffa040] active:translate-y-[1px] text-white border-2 border-[#5a3a14] shadow-[0_4px_0_#3b2509] hover:shadow-[0_3px_0_#3b2509] disabled:opacity-50"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <><Send className="w-4 h-4 mr-2" /> Submit Offer</>
        )}
      </Button>

      {!user && (
        <p className="text-[11px] text-[#e1b066] text-center mt-1">
          Tip: <button onClick={() => navigate("/")} className="underline hover:text-[#fff3d4]">log in</button> to track offers across devices.
        </p>
      )}
    </div>
  );
};

// =========================== MAIN PAGE ===========================
const OreStorePage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ categories: { Ores: [], Plates: [], Crystals: [] }, max_bonus_capacity: 20.0, store_active: true });
  const [bonusGuilds, setBonusGuilds] = useState([]);
  const [selectedGuildId, setSelectedGuildId] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [expertMode, setExpertMode] = useState(false);
  const [amounts, setAmounts] = useState({});
  const [contactIgn, setContactIgn] = useState("");
  const [contactDiscord, setContactDiscord] = useState("");
  const [notes, setNotes] = useState("");

  // Mobile bottom drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Offer success modal + meeting flow
  const [createdOffer, setCreatedOffer] = useState(null);
  const [meetingTime, setMeetingTime] = useState("");
  const [meetingNotes, setMeetingNotes] = useState("");
  const [meetingProposedISO, setMeetingProposedISO] = useState(null);
  const [savingMeeting, setSavingMeeting] = useState(false);

  // Confirm-submit popup (review screen with optional meeting-time picker)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [proposedMeetingISO, setProposedMeetingISO] = useState(null);

  // My offers
  const [myOffers, setMyOffers] = useState([]);

  // Fly animation state
  const [animations, setAnimations] = useState([]);
  const desktopStoreRef = useRef(null);
  const mobileStoreRef = useRef(null);

  const getStorePoint = useCallback(() => {
    // Pick whichever sprite is currently visible (non-zero bounding rect)
    const candidates = [desktopStoreRef.current, mobileStoreRef.current];
    for (const el of candidates) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      }
    }
    return null;
  }, []);

  const triggerFly = useCallback((info) => {
    const id = `fly_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    setAnimations((prev) => {
      // limit to last 30 to avoid DOM bloat (allows up to 25 from a single typed amount)
      const next = [...prev, { ...info, id }];
      if (next.length > 30) return next.slice(-30);
      return next;
    });
    // auto-cleanup after 1.5s as a safety
    setTimeout(() => {
      setAnimations((prev) => prev.filter((a) => a.id !== id));
    }, 1500);
  }, []);

  const removeAnimation = useCallback((id) => {
    setAnimations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const [itemsRes, guildsRes] = await Promise.all([
        axios.get(`${API_URL}/api/orestore/items`),
        axios.get(`${API_URL}/api/orestore/guilds`),
      ]);
      setData(itemsRes.data);
      setBonusGuilds(guildsRes.data?.guilds || []);
    } catch (e) {
      toast.error("Failed to load OreStore items");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyOffers = useCallback(async () => {
    try {
      const params = user ? {} : { guest_id: getGuestId() };
      const res = await axios.get(`${API_URL}/api/orestore/offers/my`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMyOffers(res.data.offers || []);
    } catch (e) {
      // silent
    }
  }, [user, token]);

  useEffect(() => {
    loadItems();
    loadMyOffers();
  }, [loadItems, loadMyOffers]);

  useEffect(() => {
    if (user?.auth_provider === "discord" && !contactDiscord) {
      setContactDiscord(user.username || "");
    }
  }, [user]); // eslint-disable-line

  const setAmount = (name, amount) => {
    setAmounts((prev) => {
      const next = { ...prev };
      if (!amount || amount <= 0) {
        delete next[name];
      } else {
        next[name] = amount;
      }
      return next;
    });
  };

  const calc = useMemo(() => {
    const allItems = [...(data.categories.Ores || []), ...(data.categories.Plates || []), ...(data.categories.Crystals || [])];
    const map = Object.fromEntries(allItems.map((i) => [i.name, i]));
    let totalBase = 0;
    let itemBonusPct = 0;
    let totalSellGross = 0; // sum of sell_price * amount (using special_price if active)
    const breakdown = [];
    Object.entries(amounts).forEach(([name, amt]) => {
      const it = map[name];
      if (!it || amt <= 0) return;
      const sub = (it.buy_price || 0) * amt;
      totalBase += sub;
      itemBonusPct += (it.bonus_per_unit || 0) * amt;
      // Effective sell price (use special_price if active)
      let sellUnit = Number(it.sell_price || 0);
      const sp = Number(it.special_price || 0);
      const until = it.special_price_until ? new Date(it.special_price_until) : null;
      if (sp > 0 && until && !Number.isNaN(until.getTime()) && until.getTime() > Date.now() && sp !== Number(it.sell_price)) {
        sellUnit = sp;
      }
      totalSellGross += sellUnit * amt;
      breakdown.push({ ...it, amount: amt, subtotal: sub, sell_unit_effective: sellUnit, sell_subtotal: sellUnit * amt });
    });
    const guild = bonusGuilds.find((g) => g.id === selectedGuildId);
    const guildBonusPct = guild ? Number(guild.bonus_pct || 0) : 0;
    const maxBonus = data.max_bonus_capacity || 20.0;
    const bonusPctRaw = itemBonusPct + guildBonusPct;
    const bonusPct = Math.min(bonusPctRaw, maxBonus);
    const bonusAmt = Math.floor(totalBase * (bonusPct / 100));
    const totalPayout = totalBase + bonusAmt;
    const taxPct = 3;
    const taxAmt = Math.floor(totalSellGross * (taxPct / 100));
    const sellAfterTax = totalSellGross - taxAmt;
    const profit = sellAfterTax - totalPayout;
    const margin = totalPayout > 0 ? (profit / totalPayout) * 100 : 0;
    return {
      breakdown,
      totalBase,
      itemBonusPct: Math.round(itemBonusPct * 100) / 100,
      guildBonusPct: Math.round(guildBonusPct * 100) / 100,
      guildName: guild ? guild.name : null,
      bonusPctRaw: Math.round(bonusPctRaw * 100) / 100,
      bonusPct: Math.round(bonusPct * 100) / 100,
      bonusAmt,
      totalPayout,
      maxBonus,
      // Expert (sell-side) numbers
      totalSellGross,
      taxPct,
      taxAmt,
      sellAfterTax,
      profit,
      margin: Math.round(margin * 100) / 100,
    };
  }, [amounts, data, bonusGuilds, selectedGuildId]);

  const itemCount = calc.breakdown.length;

  const submitOffer = async (proposedISO = null) => {
    if (calc.breakdown.length === 0) {
      toast.error("Add at least one item to your offer");
      return;
    }
    const ign = contactIgn.trim();
    const dsc = contactDiscord.trim();
    if (!ign && !dsc) {
      toast.error("Provide an IGN or Discord handle for contact");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        items: calc.breakdown.map((b) => ({ name: b.name, amount: b.amount })),
        contact_ign: ign || null,
        contact_discord: dsc || null,
        guest_id: user ? null : getGuestId(),
        notes: notes.trim() || null,
        guild_id: selectedGuildId || null,
        proposed_meeting_at: proposedISO || null,
      };
      const res = await axios.post(`${API_URL}/api/orestore/offers`, body, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast.success("Offer created! 🪨💰");
      setCreatedOffer(res.data.offer);
      setAmounts({});
      setNotes("");
      setDrawerOpen(false);
      setConfirmOpen(false);
      setProposedMeetingISO(null);
      loadMyOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create offer");
    } finally {
      setSubmitting(false);
    }
  };

  const openConfirmSubmit = () => {
    if (calc.breakdown.length === 0) {
      toast.error("Add at least one item to your offer");
      return;
    }
    const ign = contactIgn.trim();
    const dsc = contactDiscord.trim();
    if (!ign && !dsc) {
      toast.error("Provide an IGN or Discord handle for contact");
      return;
    }
    setProposedMeetingISO(null);
    setConfirmOpen(true);
  };

  const scheduleMeeting = async () => {
    if (!createdOffer) return;
    setSavingMeeting(true);
    try {
      const params = user ? {} : { guest_id: getGuestId() };
      // Derive scheduled_at human label from the picked ISO so admin sees a
      // consistent free-form date string alongside the precise UTC timestamp.
      let scheduledLabel = meetingTime;
      if (!scheduledLabel && meetingProposedISO) {
        try {
          scheduledLabel = new Date(meetingProposedISO).toLocaleString();
        } catch (e) {
          scheduledLabel = meetingProposedISO;
        }
      }
      const body = {
        scheduled_at: scheduledLabel || null,
        channel: 15,
        location: "FM 15 - CH 15",
        notes: meetingNotes || null,
        proposed_meeting_at: meetingProposedISO || null,
      };
      const res = await axios.put(
        `${API_URL}/api/orestore/offers/${createdOffer.id}/meeting`,
        body,
        {
          params,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      toast.success("Meeting scheduled — OreStore will contact you in-game");
      setCreatedOffer(res.data.offer);
      setMeetingTime("");
      setMeetingNotes("");
      setMeetingProposedISO(null);
      loadMyOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule meeting");
    } finally {
      setSavingMeeting(false);
    }
  };

  // When createdOffer changes (user opens a past offer or just submitted), seed
  // the inline edit fields from the existing meeting / proposed_meeting_at.
  useEffect(() => {
    if (createdOffer) {
      setMeetingProposedISO(createdOffer.proposed_meeting_at || null);
      setMeetingNotes(createdOffer.meeting?.notes || "");
      setMeetingTime("");
    } else {
      setMeetingProposedISO(null);
      setMeetingNotes("");
      setMeetingTime("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdOffer?.id]);

  // Re-do an existing offer: copy its items + guild + contact into the calculator state.
  const redoOffer = useCallback((offer) => {
    if (!offer) return;
    const newAmounts = {};
    (offer.items || []).forEach((it) => {
      newAmounts[it.name] = (newAmounts[it.name] || 0) + Number(it.amount || 0);
    });
    setAmounts(newAmounts);
    setSelectedGuildId(offer.guild_id || "");
    if (offer.contact_ign) setContactIgn(offer.contact_ign);
    if (offer.contact_discord) setContactDiscord(offer.contact_discord);
    setCreatedOffer(null);
    toast.success(`Loaded offer #${offer.id.slice(0, 6)} into calculator — adjust and submit again`);
    // Smooth scroll to top so the offer panel is in view (also opens drawer on mobile)
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      // open mobile drawer if narrow
      if (window.innerWidth < 1024) setDrawerOpen(true);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1f3550] text-orange-200">
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pb-32 lg:pb-8" style={{
      background: "#1c2b48",
      backgroundImage: `
        radial-gradient(ellipse at 20% 0%, rgba(80,120,180,0.18) 0%, transparent 50%),
        radial-gradient(ellipse at 80% 100%, rgba(140,90,40,0.15) 0%, transparent 50%)
      `,
    }}>
      {/* Inline keyframe styles for animations */}
      <style>{`
        @keyframes os-fly-ore {
          0%   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          70%  { transform: translate(calc(-50% + var(--dx) * 0.85), calc(-50% + var(--dy) * 0.85 - 30px)) scale(1.1); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.4); opacity: 0; }
        }
        .os-fly-ore {
          transform: translate(-50%, -50%);
          animation: os-fly-ore 1.1s cubic-bezier(0.45, 0.05, 0.55, 0.95) forwards;
        }
        @keyframes os-fly-mesos {
          0%   { transform: translate(-50%, -50%); opacity: 0; }
          15%  { transform: translate(-50%, -100%); opacity: 1; }
          100% { transform: translate(-50%, -260%); opacity: 0; }
        }
        .os-fly-mesos {
          transform: translate(-50%, -50%);
          animation: os-fly-mesos 1.3s ease-out forwards;
        }
        @keyframes os-board-sway {
          0%, 100% { transform: rotate(-2deg); }
          50%      { transform: rotate(2deg); }
        }
        .os-board-sway {
          transform-origin: top center;
          animation: os-board-sway 4s ease-in-out infinite;
        }
        /* maple star sparkle */
        @keyframes os-twinkle { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
      `}</style>

      {/* Subtle stars/dust on background */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        backgroundImage: `
          radial-gradient(circle at 10% 10%, rgba(255,255,255,0.6) 1px, transparent 2px),
          radial-gradient(circle at 30% 60%, rgba(255,220,150,0.5) 1px, transparent 2px),
          radial-gradient(circle at 75% 25%, rgba(255,255,255,0.6) 1px, transparent 2px),
          radial-gradient(circle at 85% 75%, rgba(255,220,150,0.4) 1px, transparent 2px)
        `,
        backgroundSize: "100px 100px, 140px 140px, 90px 90px, 130px 130px",
      }} />

      <div className="relative max-w-7xl mx-auto px-3 py-4 sm:px-4 sm:py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-orange-200 hover:text-white hover:bg-[#3a2814] gap-2 px-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Tracker</span>
          </Button>
          <div className="text-orange-200/80 text-[11px] sm:text-xs">
            {user ? (
              <span>Hi, <strong className="text-white">{user.username}</strong></span>
            ) : (
              <span>Browsing as guest</span>
            )}
          </div>
        </div>

        <HeaderBanner data={data} />

        {/* OreStore owner availability — online indicator + 48h schedule */}
        <OreStoreAvailabilityWidget />

        {/* Expert mode toggle (admins / orestore admins only) */}
        {user && (user.is_admin || user.is_orestore_admin) && (
          <div className="my-3 flex items-center justify-end" data-testid="orestore-expert-toggle-wrap">
            <button
              type="button"
              onClick={() => setExpertMode((v) => !v)}
              className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-md border-2 font-extrabold uppercase tracking-wider text-[11px] transition shadow-[0_3px_0_#3b2509] active:translate-y-[1px] active:shadow-[0_1px_0_#3b2509] ${
                expertMode
                  ? "bg-gradient-to-r from-amber-500 to-orange-600 border-yellow-300 text-white"
                  : "bg-[#3a2814] border-[#5a3a14] text-orange-200 hover:bg-[#4a341a]"
              }`}
              data-testid="orestore-expert-toggle"
              title="Toggle Expert Mode (admin-only): see sell prices, stock, profit & margin"
            >
              <Sparkles className={`w-3.5 h-3.5 ${expertMode ? "animate-pulse" : ""}`} />
              {expertMode ? "EXPERT MODE: ON" : "Expert Mode (Admin)"}
            </button>
          </div>
        )}

        {/* Main 3-col layout: catalog | offer panel | store sprite */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px_240px] xl:grid-cols-[1fr_360px_260px] gap-4">
          {/* LEFT: Item catalog */}
          <div className="rounded-lg border-4 border-[#5a3a14] bg-[#2a1a0e] p-3 sm:p-4 shadow-[0_4px_0_#3b2509]">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 w-full bg-[#1f1208] border-2 border-[#5a3a14] h-auto">
                {(() => {
                  const cats = ["All", ...Object.keys(data.categories)];
                  const allItems = [
                    ...(data.categories.Ores || []),
                    ...(data.categories.Plates || []),
                    ...(data.categories.Crystals || []),
                  ];
                  return cats.map((cat) => {
                    const Icon = CATEGORY_ICONS[cat] || LayoutGrid;
                    const list = cat === "All" ? allItems : (data.categories[cat] || []);
                    const count = Object.entries(amounts).filter(([n]) => list.some((i) => i.name === n)).length;
                    return (
                      <TabsTrigger
                        key={cat}
                        value={cat}
                        className="data-[state=active]:bg-[#ff8a1a] data-[state=active]:text-white text-orange-200 gap-1 sm:gap-1.5 text-[11px] sm:text-sm font-bold py-2"
                      >
                        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="hidden xs:inline sm:inline">{cat}</span>
                        {count > 0 && (
                          <span className="ml-0.5 px-1 py-0.5 rounded-full bg-[#a44dba] text-white text-[9px] font-bold">{count}</span>
                        )}
                      </TabsTrigger>
                    );
                  });
                })()}
              </TabsList>

              {/* All tab */}
              <TabsContent value="All" className="mt-3">
                {Object.entries(data.categories).map(([cat, items]) => (
                  <div key={cat} className="mb-5 last:mb-0">
                    <div className="flex items-center gap-2 mb-2">
                      {(() => {
                        const Icon = CATEGORY_ICONS[cat] || LayoutGrid;
                        return <Icon className="w-4 h-4 text-orange-300" />;
                      })()}
                      <h4 className="text-orange-200 font-extrabold text-xs uppercase tracking-[0.2em]">
                        {cat}
                      </h4>
                      <span className="h-px flex-1 bg-[#5a3a14]" />
                      <span className="text-orange-300/70 text-xs">{items.length}</span>
                    </div>
                    <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                      {items.map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          amount={amounts[item.name] || 0}
                          onChange={setAmount}
                          onAddFly={triggerFly}
                          expert={expertMode}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </TabsContent>

              {Object.entries(data.categories).map(([cat, items]) => (
                <TabsContent key={cat} value={cat} className="mt-3">
                  <div className="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
                    {items.map((item) => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        amount={amounts[item.name] || 0}
                        onChange={setAmount}
                        onAddFly={triggerFly}
                        expert={expertMode}
                      />
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* MIDDLE: Offer summary panel — desktop only */}
          <div className="hidden lg:block">
            <div className="rounded-lg border-4 border-[#5a3a14] bg-[#2a1a0e] p-4 sticky top-4 shadow-[0_4px_0_#3b2509]">
              <h3 className="text-orange-200 font-extrabold text-lg mb-3 flex items-center gap-2" style={{ fontFamily: "'Comic Sans MS', sans-serif" }}>
                <Coins className="w-5 h-5" /> Your Offer
              </h3>
              <OfferPanel
                calc={calc}
                amounts={amounts}
                setAmount={setAmount}
                contactIgn={contactIgn}
                setContactIgn={setContactIgn}
                contactDiscord={contactDiscord}
                setContactDiscord={setContactDiscord}
                notes={notes}
                setNotes={setNotes}
                user={user}
                navigate={navigate}
                submitOffer={openConfirmSubmit}
                submitting={submitting}
                data={data}
                bonusGuilds={bonusGuilds}
                selectedGuildId={selectedGuildId}
                setSelectedGuildId={setSelectedGuildId}
                expertMode={expertMode}
              />
            </div>
          </div>

          {/* RIGHT: Store sprite + wooden board */}
          <StoreSidePanel storeRef={desktopStoreRef} />
        </div>

        {/* Mobile store sprite (smaller, below catalog) */}
        <div className="lg:hidden mt-4 flex flex-col items-center gap-2">
          <img src={WOODEN_BOARD} alt="FM 15 - CH 15" className="w-32 h-auto os-board-sway" />
          <div ref={mobileStoreRef}>
            <img src={STORE_SPRITE} alt="OreStore" className="w-44 h-auto" />
          </div>
        </div>

        {/* My Offers History */}
        {myOffers.length > 0 && (
          <div className="mt-6 rounded-lg border-4 border-[#5a3a14] bg-[#2a1a0e] p-3 sm:p-4 shadow-[0_4px_0_#3b2509]">
            <h3 className="text-orange-200 font-extrabold text-base sm:text-lg mb-3" style={{ fontFamily: "'Comic Sans MS', sans-serif" }}>
              📜 Your Past Offers
            </h3>
            <div className="space-y-2">
              {myOffers.map((o) => (
                <OfferRow key={o.id} offer={o} onView={() => setCreatedOffer(o)} onRedo={redoOffer} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* MOBILE STICKY BOTTOM BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#2a1a0e] border-t-4 border-[#5a3a14] shadow-[0_-4px_12px_rgba(0,0,0,0.4)]">
        <button
          onClick={() => setDrawerOpen(true)}
          disabled={itemCount === 0}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 disabled:opacity-50"
        >
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded bg-[#ff8a1a] border-2 border-[#5a3a14] flex items-center justify-center text-white font-extrabold shadow-[0_2px_0_#3b2509]">
              {itemCount}
            </div>
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-wider text-orange-200/80 font-bold">Your offer</div>
              <div className="text-yellow-300 font-extrabold font-mono text-sm" style={{ textShadow: "1px 1px 0 #3b2509" }}>
                {formatMesos(calc.totalPayout)} mesos
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-orange-200">
            <span className="text-xs font-bold">{itemCount === 0 ? "Add items" : "View offer"}</span>
            <ChevronUp className="w-4 h-4" />
          </div>
        </button>
      </div>

      {/* MOBILE BOTTOM DRAWER */}
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent className="bg-[#2a1a0e] border-4 border-[#5a3a14] text-orange-100 max-w-md p-0 max-h-[88vh] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#5a3a14] bg-[#1f1208]">
            <h3 className="text-orange-200 font-extrabold text-lg flex items-center gap-2">
              <Coins className="w-5 h-5" /> Your Offer
            </h3>
            <button onClick={() => setDrawerOpen(false)} className="text-orange-200 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 overflow-y-auto max-h-[calc(88vh-60px)]">
            <OfferPanel
              calc={calc}
              amounts={amounts}
              setAmount={setAmount}
              contactIgn={contactIgn}
              setContactIgn={setContactIgn}
              contactDiscord={contactDiscord}
              setContactDiscord={setContactDiscord}
              notes={notes}
              setNotes={setNotes}
              user={user}
              navigate={navigate}
              submitOffer={openConfirmSubmit}
              submitting={submitting}
              data={data}
              bonusGuilds={bonusGuilds}
              selectedGuildId={selectedGuildId}
              setSelectedGuildId={setSelectedGuildId}
              expertMode={expertMode}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* CONFIRM SUBMIT — review + optional meeting picker */}
      <Dialog open={confirmOpen} onOpenChange={(v) => { if (!v) setConfirmOpen(false); }}>
        <DialogContent className="bg-[#2a1a0e] border-4 border-[#5a3a14] text-orange-100 max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-orange-100 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-amber-300" /> Review your offer
            </DialogTitle>
            <DialogDescription className="text-orange-200/70">
              Confirm the items, calculated payout, and optionally propose a
              meeting time below. Your offer will be submitted only when you
              click <strong>Submit offer</strong> at the bottom.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 mt-2 pr-1" data-testid="confirm-submit-body">
            {/* Item summary */}
            <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] p-2.5">
              <div className="font-extrabold uppercase tracking-wider text-orange-200 text-sm mb-1.5">
                Items in offer
              </div>
              <div className="space-y-1">
                {calc.breakdown.map((b) => (
                  <div
                    key={b.name}
                    className="flex items-center gap-2 text-sm"
                  >
                    <img
                      src={b.image_url}
                      alt={b.name}
                      className="w-6 h-6 object-contain"
                      style={{ imageRendering: "pixelated" }}
                    />
                    <span className="flex-1 truncate">{b.name}</span>
                    <span className="font-mono text-amber-300">
                      × {b.amount.toLocaleString()}
                    </span>
                    <span className="font-mono text-emerald-300 w-32 text-right">
                      {b.subtotal.toLocaleString()} mesos
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#5a3a14] mt-2 pt-2 text-sm space-y-0.5">
                <div className="flex justify-between text-orange-200/80">
                  <span>Base total</span>
                  <span className="font-mono">{calc.totalBase.toLocaleString()} mesos</span>
                </div>
                {calc.bonusPct > 0 && (
                  <div className="flex justify-between text-amber-200/90">
                    <span>Bonus (+{calc.bonusPct}%)</span>
                    <span className="font-mono">+{calc.bonusAmt.toLocaleString()} mesos</span>
                  </div>
                )}
                <div className="flex justify-between text-emerald-300 font-extrabold text-base pt-1">
                  <span>Total payout</span>
                  <span className="font-mono">{calc.totalPayout.toLocaleString()} mesos</span>
                </div>
              </div>
            </div>

            {/* Contact summary */}
            <div className="rounded-lg border-2 border-[#5a3a14] bg-[#1f1208] p-2.5 text-sm">
              <div className="font-extrabold uppercase tracking-wider text-orange-200 mb-1.5">
                Contact
              </div>
              <div className="space-y-0.5 text-orange-100/90">
                {contactIgn && (
                  <div>
                    <span className="text-orange-200/70">IGN:</span>{" "}
                    <strong>{contactIgn}</strong>
                  </div>
                )}
                {contactDiscord && (
                  <div>
                    <span className="text-orange-200/70">Discord:</span>{" "}
                    <strong>{contactDiscord}</strong>
                  </div>
                )}
                {notes && (
                  <div className="text-[12px] text-orange-200/80 mt-1 italic">
                    "{notes}"
                  </div>
                )}
              </div>
            </div>

            {/* Meeting time picker */}
            <MeetingTimePickerInline
              value={proposedMeetingISO}
              onChange={setProposedMeetingISO}
            />
          </div>

          <DialogFooter className="shrink-0 mt-3 gap-2 flex-row justify-end">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              data-testid="confirm-cancel"
            >
              Back to edit
            </Button>
            <Button
              onClick={() => submitOffer(proposedMeetingISO)}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="confirm-submit"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OFFER CREATED / MEETING MODAL */}
      <Dialog open={!!createdOffer} onOpenChange={(v) => { if (!v) setCreatedOffer(null); }}>
        <DialogContent className="bg-[#2a1a0e] border-4 border-[#5a3a14] text-orange-100 max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-orange-200 text-xl flex items-center gap-2" style={{ fontFamily: "'Comic Sans MS', sans-serif" }}>
              <img src={ORESTORE_LOGO} alt="OreStore" className="h-7 w-auto" />
              <span>Offer #{createdOffer?.id?.slice(0, 8)}</span>
            </DialogTitle>
            <DialogDescription className="text-orange-200/70">
              Status: <span className="font-bold text-orange-100">{createdOffer?.status}</span> — schedule a time to finalize the trade.
            </DialogDescription>
          </DialogHeader>

          {createdOffer && (
            <div className="space-y-3 overflow-y-auto pr-1 -mr-1">
              {/* Items list */}
              <div className="bg-[#1f1208] rounded-lg p-3 border-2 border-[#5a3a14]">
                <div className="text-xs uppercase tracking-wider text-orange-200/80 mb-2 font-bold">Items in offer</div>
                <div className="space-y-1">
                  {(createdOffer.breakdown || createdOffer.items || []).map((b, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      {b.image_url && <img src={b.image_url} alt="" className="w-6 h-6" style={{ imageRendering: "pixelated" }} />}
                      <span className="text-orange-100 flex-1 font-bold">{b.name}</span>
                      <span className="font-mono text-orange-300">×{b.amount}</span>
                      {b.subtotal != null && <span className="font-mono text-yellow-200 w-32 text-right font-bold">{formatMesos(b.subtotal)}</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-[#5a3a14] text-sm space-y-0.5">
                  <div className="flex justify-between"><span>Base</span><span className="font-mono">{formatMesos(createdOffer.total_base)}</span></div>
                  <div className="flex justify-between text-fuchsia-200"><span>Bonus ({createdOffer.bonus_percent}%)</span><span className="font-mono">+{formatMesos(createdOffer.bonus_amount)}</span></div>
                  <div className="flex justify-between font-extrabold text-yellow-300"><span>Total payout</span><span className="font-mono">{formatMesos(createdOffer.total_payout)}</span></div>
                </div>
              </div>

              {/* Existing meeting if any */}
              {createdOffer.meeting && createdOffer.meeting.scheduled_at && (
                <div className="bg-emerald-900/40 border-2 border-emerald-500 rounded p-3 text-sm space-y-1">
                  <div className="text-emerald-200 font-extrabold flex items-center gap-1"><Calendar className="w-4 h-4" /> Meeting scheduled</div>
                  <div>🕐 {createdOffer.meeting.scheduled_at}</div>
                  <div>📍 FM 15 — CH 15</div>
                  {createdOffer.meeting.notes && <div className="italic text-emerald-200/80">📝 {createdOffer.meeting.notes}</div>}
                </div>
              )}

              {/* Trade location notice with wooden board */}
              <div className="bg-[#3a2814] border-2 border-orange-500/50 rounded p-3 flex gap-3 items-center">
                <img src={WOODEN_BOARD} alt="FM 15 - CH 15" className="w-20 h-auto shrink-0 os-board-sway" />
                <div className="text-sm text-orange-100/95 flex-1">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-orange-300 shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-orange-200">Trade location:</strong> the OreStore meets sellers at <strong className="text-orange-200">Free Market 15 — Channel 15</strong>.
                      <br />
                      <span className="text-orange-200/80 text-xs">After scheduling a time, our staff will contact you in-game (via your IGN) or on Discord.</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Schedule meeting form */}
              <div className="bg-[#1f1208] rounded-lg p-3 border-2 border-[#5a3a14] space-y-2">
                <div className="text-xs uppercase tracking-wider text-orange-200/80 font-bold flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Organize / update meeting
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-orange-100/90 font-bold mb-1 block">
                      Pick a time on the calendar
                    </Label>
                    <MeetingTimePickerInline
                      value={meetingProposedISO}
                      onChange={setMeetingProposedISO}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-100/90 font-bold">
                      Or type a custom time (free-form, overrides the picker)
                    </Label>
                    <Input
                      value={meetingTime}
                      onChange={(e) => setMeetingTime(e.target.value.slice(0, 64))}
                      placeholder="e.g. Today 20:00 GMT+2 or 2025-07-14 18:30"
                      className="bg-white border-2 border-[#5a3a14] text-[#3b2509] mt-1 h-9 font-bold"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-100/90 font-bold">Notes (optional)</Label>
                    <Input
                      value={meetingNotes}
                      onChange={(e) => setMeetingNotes(e.target.value.slice(0, 300))}
                      placeholder="e.g. I'll be late if not online"
                      className="bg-white border-2 border-[#5a3a14] text-[#3b2509] mt-1 h-9 font-bold"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 shrink-0">
            <Button variant="ghost" onClick={() => setCreatedOffer(null)} className="text-orange-200">
              Close
            </Button>
            <Button
              onClick={scheduleMeeting}
              disabled={savingMeeting}
              className="bg-[#ff8a1a] hover:bg-[#ffa040] text-white font-extrabold border-2 border-[#5a3a14] shadow-[0_3px_0_#3b2509] active:translate-y-[1px]"
            >
              {savingMeeting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Calendar className="w-4 h-4 mr-2" /> Save meeting</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FLYING ORE OVERLAY */}
      <FlyOverlay
        animations={animations}
        onAnimationEnd={removeAnimation}
        storePoint={getStorePoint()}
      />
    </div>
  );
};

const OfferRow = ({ offer, onView, onRedo }) => {
  const statusColors = {
    pending: "bg-orange-500/30 text-orange-100 border-orange-500",
    scheduled: "bg-emerald-500/30 text-emerald-100 border-emerald-500",
    completed: "bg-blue-500/30 text-blue-100 border-blue-500",
    cancelled: "bg-red-500/30 text-red-100 border-red-500",
  };
  // Format meeting time (prefer ISO proposed_meeting_at over free-form scheduled_at)
  let meetingLabel = null;
  if (offer.proposed_meeting_at) {
    try {
      const d = new Date(offer.proposed_meeting_at);
      meetingLabel = d.toLocaleString(undefined, {
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      meetingLabel = offer.proposed_meeting_at;
    }
  } else if (offer.meeting?.scheduled_at) {
    meetingLabel = offer.meeting.scheduled_at;
  }
  const canEditMeeting = offer.status !== "completed" && offer.status !== "cancelled";
  return (
    <div
      className="flex items-center gap-3 p-3 rounded border-2 border-[#5a3a14] bg-[#1f1208] hover:bg-[#3a2814] hover:border-orange-500 transition"
    >
      <div className="text-orange-300/80 font-mono text-xs hidden sm:block">#{offer.id.slice(0, 6)}</div>
      <button
        type="button"
        onClick={onView}
        className="flex-1 min-w-0 text-left"
      >
        <div className="text-orange-100 text-sm truncate">
          {(offer.items || []).length} item{(offer.items || []).length !== 1 ? "s" : ""} •
          <span className="text-yellow-300 font-mono ml-1 font-bold">{formatMesos(offer.total_payout)}</span>
        </div>
        <div className="text-[11px] text-orange-300/60 flex items-center gap-2 flex-wrap">
          <span>{new Date(offer.created_at).toLocaleString()}</span>
          {meetingLabel && (
            <span className="inline-flex items-center gap-1 text-emerald-300 font-bold">
              <Calendar className="w-3 h-3" />
              {meetingLabel}
            </span>
          )}
        </div>
      </button>
      <span className={`text-[10px] uppercase font-extrabold px-2 py-1 rounded border-2 ${statusColors[offer.status] || statusColors.pending}`}>
        {offer.status}
      </span>
      {canEditMeeting && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onView(); }}
          className="text-[10px] uppercase font-extrabold px-2 py-1 rounded border-2 border-emerald-500 text-emerald-200 hover:bg-emerald-500/20 transition flex items-center gap-1"
          title="Open offer to edit meeting time"
          data-testid={`offer-edit-meeting-${offer.id}`}
        >
          <Calendar className="w-3 h-3" /> {meetingLabel ? "Edit time" : "Set time"}
        </button>
      )}
      {onRedo && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRedo(offer); }}
          className="text-[10px] uppercase font-extrabold px-2 py-1 rounded border-2 border-yellow-500 text-yellow-200 hover:bg-yellow-500/20 transition"
          title="Load this offer into the calculator and submit a new one"
          data-testid={`offer-redo-${offer.id}`}
        >
          ↻ Re-do
        </button>
      )}
    </div>
  );
};

export default OreStorePage;
